import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Headphones, 
  Mail, 
  Phone, 
  Globe, 
  Building2,
  Code2,
  Calendar,
  Shield,
  Heart
} from "lucide-react";
import axios from "axios";

import { API_BASE_URL } from '../utils/api';
const API = API_BASE_URL;
const APP_VERSION = "1.0.0";
const BUILD_DATE = "January 2025";

export default function About() {
  const { token } = useAuth();
  const [developerInfo, setDeveloperInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const fetchDeveloperInfo = async () => {
      try {
        const response = await axios.get(`${API}/api/settings/developer-info`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDeveloperInfo(response.data);
      } catch (error) {
        console.warn("Could not fetch developer info:", error);
        // Use default values
        setDeveloperInfo({
          company_name: "Connect-US",
          contact_email: "support@connect-us.co.nz",
          contact_phone: "",
          website: "https://connect-us.co.nz"
        });
      } finally {
        setLoading(false);
      }
    };

    fetchDeveloperInfo();
  }, [token]);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">About CONNECT</h1>
        <p className="text-muted-foreground">Cloud PBX Operator Console</p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* App Info Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Headphones className="w-5 h-5 text-primary" />
              Application Info
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Version</span>
              <Badge variant="secondary" className="font-mono">{APP_VERSION}</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Build Date</span>
              <span className="text-sm">{BUILD_DATE}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Platform</span>
              <span className="text-sm">Web Application</span>
            </div>
            <div className="pt-4 border-t">
              <p className="text-sm text-muted-foreground">
                CONNECT is a modern Cloud PBX Operator Console designed to streamline 
                call management, improve team collaboration, and enhance customer service.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Developer Info Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Code2 className="w-5 h-5 text-primary" />
              Developer
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {loading ? (
              <div className="animate-pulse space-y-3">
                <div className="h-4 bg-muted rounded w-3/4"></div>
                <div className="h-4 bg-muted rounded w-1/2"></div>
              </div>
            ) : (
              <>
                {developerInfo?.company_name && (
                  <div className="flex items-center gap-3">
                    <Building2 className="w-4 h-4 text-muted-foreground" />
                    <span>{developerInfo.company_name}</span>
                  </div>
                )}
                {developerInfo?.contact_email && (
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <a 
                      href={`mailto:${developerInfo.contact_email}`}
                      className="text-primary hover:underline"
                    >
                      {developerInfo.contact_email}
                    </a>
                  </div>
                )}
                {developerInfo?.contact_phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <a 
                      href={`tel:${developerInfo.contact_phone}`}
                      className="text-primary hover:underline"
                    >
                      {developerInfo.contact_phone}
                    </a>
                  </div>
                )}
                {developerInfo?.website && (
                  <div className="flex items-center gap-3">
                    <Globe className="w-4 h-4 text-muted-foreground" />
                    <a 
                      href={developerInfo.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      {developerInfo.website}
                    </a>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Features Card */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-primary" />
              Key Features
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {[
                { title: "WebRTC Softphone", desc: "Make and receive calls directly in your browser" },
                { title: "Contact Management", desc: "Manage internal and external contacts" },
                { title: "Call Transfer", desc: "Blind and attended transfer options" },
                { title: "Queue Monitoring", desc: "Real-time view of active calls" },
                { title: "Call Analytics", desc: "Track call volumes and performance" },
                { title: "Global Contacts", desc: "Shared company-wide directory" },
              ].map((feature, i) => (
                <div key={i} className="p-3 rounded-lg bg-muted/50">
                  <h4 className="font-medium mb-1">{feature.title}</h4>
                  <p className="text-sm text-muted-foreground">{feature.desc}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Legal Links */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              Legal
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              <Link to="/app/privacy" className="text-primary hover:underline">Privacy Policy</Link>
              <span className="text-muted-foreground">•</span>
              <Link to="/app/terms" className="text-primary hover:underline">Terms & Conditions</Link>
              <span className="text-muted-foreground">•</span>
              <Link to="/app/help" className="text-primary hover:underline">Help & Support</Link>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <div className="mt-8 text-center text-sm text-muted-foreground">
        <p>© {new Date().getFullYear()} {developerInfo?.company_name || "Connect-US"}. All rights reserved.</p>
      </div>
    </div>
  );
}
